import { Alert, Box } from "@mantine/core";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetRetailerNotificationQuery } from "~/api/hooks/retailerNotification/useGetRetailerNotificationQuery";
import { useSetRetailerNotificationAsRead } from "~/api/hooks/retailerNotification/useSetRetailerNotificationAsRead";
import type {
  MarkRetailerNotificationAsReadRequest,
  RetailerNotificationMessage,
} from "~/api/openapi";
import { HtmlRenderer } from "~/ui/components/HtmlRenderer/HtmlRenderer";
import RetailerNotificationStyles from "./RetailerNotifications.module.css";

const isWithinDisplayPeriod = (displayPeriod: RetailerNotificationMessage["displayPeriod"]) => {
  const currentDate = new Date();
  const startDate = new Date(displayPeriod.displayFrom || new Date());
  const endDate = new Date(displayPeriod.displayUntil || new Date());
  return currentDate >= startDate && currentDate <= endDate;
};

const RetailerNotifications: React.FC = () => {
  const allowedTags = ["br", "a", "b", "strong", "h1"];
  const { i18n } = useTranslation();
  const { data: retailerNotificationsData, isFetched: isRetailerNotificationsFetched } =
    useGetRetailerNotificationQuery({
      langCode: i18n.language.toUpperCase(),
    });

  const [retailerNotifications, setRetailerNotifications] = useState<RetailerNotificationMessage[]>(
    [],
  );

  const { mutate: updateNotificationReadStatus } = useSetRetailerNotificationAsRead();

  const markNotificationAsRead = (
    notificationId: RetailerNotificationMessage["notificationId"],
  ) => {
    const args: MarkRetailerNotificationAsReadRequest = {
      notificationId: notificationId,
    };
    updateNotificationReadStatus(args);
  };

  useEffect(() => {
    if (!isRetailerNotificationsFetched || !retailerNotificationsData?.length) {
      return;
    }

    setRetailerNotifications(
      retailerNotificationsData.filter((notification: RetailerNotificationMessage) =>
        isWithinDisplayPeriod(notification.displayPeriod),
      ),
    );
  }, [isRetailerNotificationsFetched, retailerNotificationsData]);

  const dismissNotification = (notificationId: RetailerNotificationMessage["notificationId"]) => {
    setRetailerNotifications(
      retailerNotifications?.filter(
        (retailerNotification: RetailerNotificationMessage) =>
          retailerNotification.notificationId !== notificationId,
      ),
    );
  };

  const getNotificationColor = (displayClass?: string): string => {
    switch (displayClass?.toLowerCase()) {
      case "outline-green":
        return "green";
      case "outline-blue":
        return "blue";
      default:
        return "red";
    }
  };

  return (
    <>
      {retailerNotifications?.map((notification: RetailerNotificationMessage) => {
        return (
          <Alert
            key={notification.notificationId}
            variant="light"
            color={getNotificationColor(notification.displayClass)}
            withCloseButton={notification.acknowledgeable}
            onClose={
              notification.acknowledgeable
                ? () => {
                    dismissNotification(notification.notificationId);
                    markNotificationAsRead(notification.notificationId);
                  }
                : undefined
            }
          >
            <Box className={RetailerNotificationStyles.notificationBox}>
              <HtmlRenderer tags={allowedTags}>{notification.message}</HtmlRenderer>
            </Box>
          </Alert>
        );
      })}
    </>
  );
};

export { RetailerNotifications };
